import { useEffect, useState } from 'react'
import { TreatmentName, treatmentFlagValues } from '../constants/treatments'
import useIsRolloutPlanSettled from 'hooks/treatments/useIsRolloutPlanSettled'
import { getReleasedTreatment } from 'lib/splitRolloutPlan'
import {
  isSplitAnalyticsConsentGranted,
  userSplitClient,
} from '../utils/treatment'

export interface GetUserTreatmentModel<T> {
  treatmentValue: T
  isUserSplitReady: boolean
}

// Gotcha: awaits userSplitClient.ready() with no .catch, so it hangs on
// SDK_READY_TIMED_OUT; incomplete (see TODO). Not safe for route guards yet —
// prefer useGetUserTreatment + useIsUserTreatmentResolving.
//TODO: make use of useNewGetUserTreatment hook over useGetUserTreatment once this done then this hook name will
//rename to useGetUserTreatment
const useNewGetUserTreatment = <T>(
  name: TreatmentName | Array<TreatmentName>,
  allowToFetchTreatment = true
): GetUserTreatmentModel<T> => {
  const rolloutPlanSettled = useIsRolloutPlanSettled()
  const [state, setState] = useState<GetUserTreatmentModel<T>>({
    treatmentValue: null,
    isUserSplitReady: false,
  })

  const getUserTreatment = async () => {
    let value

    if (Array.isArray(name)) {
      value = await userSplitClient
        .ready()
        .then(() => userSplitClient.getTreatments(name))
    } else {
      value = await userSplitClient
        .ready()
        .then(() => userSplitClient.getTreatment(name))
    }

    setState({
      isUserSplitReady: true,
      treatmentValue: value,
    })
  }

  const resolveFromReleasedPlan = () => {
    const value = Array.isArray(name)
      ? name.reduce<Record<string, string>>((acc, flagName) => {
          acc[flagName] =
            getReleasedTreatment(flagName) ?? treatmentFlagValues.CONTROL

          return acc
        }, {})
      : getReleasedTreatment(name) ?? treatmentFlagValues.CONTROL

    setState({
      isUserSplitReady: true,
      treatmentValue: (value as unknown) as T,
    })
  }

  useEffect(() => {
    if (!allowToFetchTreatment) {
      return
    }

    if (userSplitClient) {
      getUserTreatment()

      return
    }

    // Consent denied and no user client: resolve from the rollout plan once
    // settled so a 100% released ON is not lost.
    if (!isSplitAnalyticsConsentGranted() && rolloutPlanSettled) {
      resolveFromReleasedPlan()
    }
  }, [userSplitClient, allowToFetchTreatment, rolloutPlanSettled])

  return state
}

export default useNewGetUserTreatment
